import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../pages/UserContext";
import useLogout from "./useLogout.hook";
import {
  ACTIVITY_STORAGE_KEY,
  INACTIVITY_TIMEOUT_MS,
  INACTIVITY_WARNING_DURATION_MS,
} from "../utlities/core.constants";

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointermove",
];

/**
 * Automatically logs the user out after {@link INACTIVITY_TIMEOUT_MS} of no
 * interaction.  Attaches lightweight passive listeners on the document for
 * common user-activity events and resets inactivity timers on each.
 *
 * Activity is synchronised across browser tabs via localStorage so that
 * interaction in one tab keeps all tabs alive.
 *
 * This hook can be mounted globally; it only runs timers while a valid token
 * exists.
 */
export default function useInactivityLogout() {
  const { token } = useContext(UserContext);
  // Reuse the shared logout hook so an inactivity timeout ends the
  // Login.gov SSO session the same way the explicit "Log Out" action does,
  // instead of only clearing local app state.
  const { logout } = useLogout();
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const isWarningOpenRef = useRef(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  useEffect(() => {
    isWarningOpenRef.current = isWarningOpen;
  }, [isWarningOpen]);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const startTimers = useCallback((timeoutMs = INACTIVITY_TIMEOUT_MS) => {
    clearTimers();
    const warningDelay = Math.max(timeoutMs - INACTIVITY_WARNING_DURATION_MS, 0);

    if (warningDelay > 0) {
      warningTimerRef.current = setTimeout(() => {
        setIsWarningOpen(true);
      }, warningDelay);
    }

    logoutTimerRef.current = setTimeout(() => {
      setIsWarningOpen(false);
      logout();
    }, timeoutMs);
  }, [clearTimers, logout]);

  const getLastActivityMs = useCallback(() => {
    try {
      const value = localStorage.getItem(ACTIVITY_STORAGE_KEY);
      if (!value) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, []);

  const setLastActivityMs = useCallback((lastActivityMs) => {
    try {
      localStorage.setItem(ACTIVITY_STORAGE_KEY, lastActivityMs.toString());
    } catch { /* localStorage may be unavailable */ }
  }, []);

  const scheduleFromLastActivity = useCallback((lastActivityMs) => {
    const elapsedMs = Date.now() - lastActivityMs;
    const remainingMs = INACTIVITY_TIMEOUT_MS - elapsedMs;

    if (remainingMs <= 0) {
      clearTimers();
      setIsWarningOpen(false);
      logout();
      return false;
    }

    setIsWarningOpen(remainingMs <= INACTIVITY_WARNING_DURATION_MS);
    startTimers(remainingMs);
    return true;
  }, [clearTimers, logout, startTimers]);

  const continueSession = useCallback(() => {
    const nowMs = Date.now();
    setLastActivityMs(nowMs);
    scheduleFromLastActivity(nowMs);
  }, [scheduleFromLastActivity, setLastActivityMs]);

  const onLocalActivity = useCallback(() => {
    // Keep warning visible until user explicitly selects Continue.
    if (isWarningOpenRef.current) return;
    const nowMs = Date.now();
    setLastActivityMs(nowMs);
    scheduleFromLastActivity(nowMs);
  }, [scheduleFromLastActivity, setLastActivityMs]);

  useEffect(() => {
    if (!token || process.env.REACT_APP_DISABLE_INACTIVITY_LOGOUT === "true") {
      // The session can end without going through our own logout() above — e.g. a 401
      // response elsewhere calls setJwt(null) directly. Whenever we no longer have a
      // token, make sure the warning modal can't stay stranded open and no stale timers
      // are left running.
      clearTimers();
      setIsWarningOpen(false);
      return undefined;
    }

    setIsWarningOpen(false);
    const existingLastActivityMs = getLastActivityMs();
    const lastActivityMs = existingLastActivityMs ?? Date.now();
    if (existingLastActivityMs === null) {
      setLastActivityMs(lastActivityMs);
    }
    const sessionStillActive = scheduleFromLastActivity(lastActivityMs);
    if (!sessionStillActive) {
      return undefined;
    }

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, onLocalActivity, { passive: true })
    );

    const onStorageChange = (e) => {
      if (e.key !== ACTIVITY_STORAGE_KEY || !e.newValue) return;
      if (isWarningOpenRef.current) return;
      const parsed = Number(e.newValue);
      if (!Number.isFinite(parsed)) return;
      scheduleFromLastActivity(parsed);
    };
    window.addEventListener("storage", onStorageChange);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, onLocalActivity)
      );
      window.removeEventListener("storage", onStorageChange);
    };
  }, [token, clearTimers, getLastActivityMs, setLastActivityMs, scheduleFromLastActivity, onLocalActivity]);

  return {
    isWarningOpen,
    continueSession,
  };
}
